/* eslint-disable no-param-reassign */
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  api,
  useCancelSubscriptionMutation,
  useCreateCheckoutMutation,
  useGetMeQuery,
  useGetPackagesQuery,
  useGetPricesQuery,
  useGetSubscriptionQuery,
  UserWithSubscriptions,
} from '@api/apiCompat';
import { ERROR, SUCCESS, USER_ACCESS_TOKEN } from '@constants/auth';
import { PERIOD } from '@constants/billing';
import {
  API_ERROR_MSG_PATH,
  BILLING_ENTERPRISE_TOKENS_AMOUNT,
  BILLING_FREE_TOKENS_AMOUNT,
  BILLING_GROWTH_TOKENS_AMOUNT,
  BILLING_PRO_TOKENS_AMOUNT,
  BILLING_SCALE_TOKENS_AMOUNT,
  BILLING_STARTER_TOKENS_AMOUNT,
  CONTACT_US_EMAIL,
} from '@constants/common';
import { ROUTING } from '@constants/routing';
import NiceModal from '@ebay/nice-modal-react';
import { GetPackagesResponse, GetPricesResponse, GetSubscriptionsResponse, SortedPackage } from '@pages/Settings/types';
import { convertFromCents, isTrialExpired } from '@pages/Settings/utils';
import { RootState } from '@store/store';
import { formatCurrency } from '@utils/formatCurrency';
import { getErrorMessage } from '@utils/getMessage';
import { useSnackbar } from 'notistack';

import { amber, blue, deepPurple, grey } from '@mui/material/colors';
import { BillingConfirmModalId } from '@components/BillingConfirmModal';

import { ConfirmModalId } from '@/shared/ConfirmModal/ConfirmModal';
import { PeriodValue } from '@/shared/types';

const colors = [amber[50], deepPurple[50], grey[200]];

export interface PackageConfig {
  id: string;
  isActive: boolean;
  bgColor: string;
  title: string;
  subtitle: string;
  packagePriceTitle: string;
  /** Optional small caption shown below the headline price (e.g. "Billed annually"). */
  packagePriceSubtitle?: string;
  /** Pre-discount price shown with strikethrough when annual is active (e.g. "$29/month"). */
  originalPriceTitle?: string;
  /** Annual-savings sub-line shown below the price when annual is active (e.g. "Save $72/year"). */
  annualSavings?: string;
  isFree?: boolean;
  tokensInPackage: number;
  onClick: () => void;
  onCancelClick: (e: MouseEvent) => void;
  isExpired?: boolean;
  isLoading?: boolean;
  isPopular?: boolean;
  packageButtonLocale?: string;
  isDisabledPlanSwitch?: boolean;
}

export const useBilling = (period: PeriodValue = PERIOD.month) => {
  const [activePrice, setActivePrice] = useState(0);
  const snackbar = useSnackbar();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Pricing is a public page; when an unauthenticated visitor is browsing,
  // we skip the user/subscription queries (which would 401) and instead
  // bounce plan-card clicks to /signup.
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const hasToken = typeof window !== 'undefined' && !!localStorage.getItem(USER_ACCESS_TOKEN);
  const isLoggedIn = isAuthenticated || hasToken;

  const { data: packagesData, isLoading: isLoadingPackages } = useGetPackagesQuery(undefined, { refetchOnFocus: true });
  const { data: pricesData, isLoading: isLoadingPrices } = useGetPricesQuery(undefined, { refetchOnFocus: true });
  const { data: meData, isLoading: isLoadingUser } = useGetMeQuery(undefined, {
    refetchOnFocus: true,
    skip: !isLoggedIn,
  });
  // Cast to extended user type with subscriptions (not in OpenAPI schema)
  const me = meData as unknown as UserWithSubscriptions | undefined;
  const {
    data: subscriptionData,
    isLoading: isLoadingSubs,
    error: subscriptionError,
  } = useGetSubscriptionQuery(undefined, { refetchOnFocus: true, skip: !isLoggedIn });

  const [subscribeMutation, { isLoading: isLoadingSubscription }] = useCreateCheckoutMutation();
  const [cancelSubscriptionMutation, { isLoading: isLoadingCancelSubscription }] = useCancelSubscriptionMutation();

  const handleCheckout = useCallback(
    async (priceId: string, onSuccess?: () => void) => {
      try {
        // Stripe redirects to these URLs after the Checkout session resolves.
        // Override the Django defaults (`${FRONTEND_URL}?checkout=success`)
        // so users land on the dedicated `/checkout/success` page rendered
        // inside the authenticated app shell instead of the marketing
        // landing page. Stripe substitutes `{CHECKOUT_SESSION_ID}` for us.
        const origin = typeof window !== 'undefined' ? window.location.origin : '';
        const successUrl = `${origin}/${ROUTING.CHECKOUT_SUCCESS}?session_id={CHECKOUT_SESSION_ID}`;
        const cancelUrl = `${origin}/${ROUTING.CHECKOUT_CANCELLED}`;

        // Generated API expects 'checkoutRequestRequest' with 'plan' field (from OpenAPI schema)
        // The backend checkout endpoint accepts 'plan' as the price ID to subscribe to
        const response = await (subscribeMutation as any)({
          checkoutRequestRequest: {
            plan: priceId, // Use 'plan' field as expected by generated types
            success_url: successUrl,
            cancel_url: cancelUrl,
          },
        }).unwrap();

        const checkoutResponse = response as { url: string; mock?: boolean; tokens_added?: number; message?: string };

        // Handle mock checkout - don't redirect, refetch balance and navigate to campaigns
        if (checkoutResponse.mock) {
          // Force refetch the balance to trigger the animation
          dispatch(api.endpoints.billingBalanceRetrieve.initiate(undefined, { forceRefetch: true }) as any);
          snackbar.enqueueSnackbar(
            checkoutResponse.message || `Successfully added ${checkoutResponse.tokens_added?.toLocaleString()} tokens!`,
            { variant: SUCCESS },
          );
          onSuccess?.();
          // Navigate to campaigns page with success params to trigger animation there
          navigate(`/${ROUTING.ALL_CAMPAIGNS}?checkout=mock_success&tokens=${checkoutResponse.tokens_added}`);
          return;
        }

        // Real Stripe checkout - redirect to payment page
        window.location.href = checkoutResponse.url;
      } catch (err) {
        snackbar.enqueueSnackbar(getErrorMessage(err, API_ERROR_MSG_PATH), { variant: ERROR });
      }
    },
    [dispatch, navigate, snackbar, subscribeMutation],
  );

  const handleTopup = useCallback(
    async (priceId: string, onSuccess?: () => void) => {
      try {
        // Same dedicated success/cancel pages as the subscription flow.
        const origin = typeof window !== 'undefined' ? window.location.origin : '';
        const successUrl = `${origin}/${ROUTING.CHECKOUT_SUCCESS}?session_id={CHECKOUT_SESSION_ID}`;
        const cancelUrl = `${origin}/${ROUTING.CHECKOUT_CANCELLED}`;

        // Topup also uses the checkout flow to create a Stripe session for one-time purchase
        // The backend will handle the token addition after Stripe payment completes
        const response = await (subscribeMutation as any)({
          checkoutRequestRequest: {
            plan: priceId, // Use 'plan' field as expected by generated types
            success_url: successUrl,
            cancel_url: cancelUrl,
          },
        }).unwrap();

        const checkoutResponse = response as { url: string; mock?: boolean; tokens_added?: number; message?: string };

        // Handle mock checkout - don't redirect, refetch balance and navigate to campaigns
        if (checkoutResponse.mock) {
          // Force refetch the balance to trigger the animation
          dispatch(api.endpoints.billingBalanceRetrieve.initiate(undefined, { forceRefetch: true }) as any);
          snackbar.enqueueSnackbar(
            checkoutResponse.message || `Successfully added ${checkoutResponse.tokens_added?.toLocaleString()} tokens!`,
            { variant: SUCCESS },
          );
          onSuccess?.();
          // Navigate to campaigns page with success params to trigger animation there
          navigate(`/${ROUTING.ALL_CAMPAIGNS}?checkout=mock_success&tokens=${checkoutResponse.tokens_added}`);
          return;
        }

        // Real Stripe checkout - redirect to payment page
        window.location.href = checkoutResponse.url;
      } catch (err) {
        snackbar.enqueueSnackbar(getErrorMessage(err, API_ERROR_MSG_PATH), { variant: ERROR });
      }
    },
    [dispatch, navigate, snackbar, subscribeMutation],
  );

  const handleCancelSubscription = useCallback(async () => {
    try {
      await cancelSubscriptionMutation();
    } catch (err) {
      snackbar.enqueueSnackbar(getErrorMessage(err, API_ERROR_MSG_PATH), { variant: ERROR });
    }
  }, [cancelSubscriptionMutation, snackbar]);

  const convertedPrices = useMemo(() => {
    // Always use monthly prices as the base
    const monthlyPrices = ((pricesData as GetPricesResponse)?.data || []).filter(
      price => price.recurring?.interval === PERIOD.month,
    );

    // Calculate prices based on selected period
    return monthlyPrices.map(item => {
      // For annual: apply 20% discount and show monthly equivalent
      const baseAmount = item.unit_amount;
      const discountedAmount =
        period === PERIOD.year
          ? Math.round(baseAmount * 0.8) // 20% discount for annual
          : baseAmount;

      return {
        ...item,
        unit_amount: formatCurrency(convertFromCents(discountedAmount)),
        unit_amount_decimal: String(discountedAmount),
        // Keep track of original for comparisons
        original_unit_amount: item.unit_amount,
      };
    });
  }, [period, pricesData]);

  const isLoadingMutation = [isLoadingSubscription, isLoadingCancelSubscription].some(Boolean);
  const isLoadingQueries = [isLoadingPackages, isLoadingPrices, isLoadingUser, isLoadingSubs].some(Boolean);

  const config: PackageConfig[] = useMemo(() => {
    // Public/marketing fallback: the Stripe-backed packages/prices APIs are
    // auth-gated, so unauthenticated visitors hit /pricing with no plan data
    // unless we fall back to a static catalog. Prices mirror PLAN_LABELS in
    // src/constants/plans.ts.
    if (!isLoggedIn) {
      const PLAN_DESCRIPTIONS: Record<string, string> = {
        starter: 'Everything in Free; SMTP/IMAP accounts; AI email warmup; Priority email support',
        growth: 'Everything in Starter; Custom domains & DNS; Multi-account warmup pool; Most popular',
        professional: 'Everything in Growth; 25K leads/mo; Priority queue; Onboarding assistance',
        scale: 'Everything in Pro; 50K leads/mo; VIP scrape queue; Dedicated success manager',
        enterprise: 'Everything in Scale; 100K leads/mo; Custom integrations; SLA + onboarding',
      };
      const baseMonthly: { id: string; name: string; tokens: number; price: number }[] = [
        { id: 'starter', name: 'Starter', tokens: BILLING_STARTER_TOKENS_AMOUNT, price: 29 },
        { id: 'growth', name: 'Growth', tokens: BILLING_GROWTH_TOKENS_AMOUNT, price: 59 },
        { id: 'professional', name: 'Pro', tokens: BILLING_PRO_TOKENS_AMOUNT, price: 149 },
        { id: 'scale', name: 'Scale', tokens: BILLING_SCALE_TOKENS_AMOUNT, price: 249 },
        { id: 'enterprise', name: 'Enterprise', tokens: BILLING_ENTERPRISE_TOKENS_AMOUNT, price: 399 },
      ];
      const publicCards: PackageConfig[] = [
        {
          id: 'free',
          isActive: false,
          bgColor: blue[50],
          title: 'billing.packageTitle.free',
          subtitle: 'billing.packageText.free',
          packagePriceTitle: 'billing.price.free',
          isFree: true,
          tokensInPackage: BILLING_FREE_TOKENS_AMOUNT,
          onClick: () => navigate(`/${ROUTING.SIGNUP}`),
          onCancelClick: () => {},
          packageButtonLocale: 'billing.other.getStarted',
        },
        ...baseMonthly.map(p => {
          const isAnnual = period === PERIOD.year;
          const monthlyPrice = isAnnual ? Math.round(p.price * 0.8) : p.price;
          const annualSavings = isAnnual ? (p.price - monthlyPrice) * 12 : 0;
          return {
            id: p.id,
            isActive: false,
            bgColor: grey[200],
            title: p.name,
            subtitle: PLAN_DESCRIPTIONS[p.id] ?? '',
            packagePriceTitle: `$${monthlyPrice}/month`,
            packagePriceSubtitle: isAnnual ? 'Billed annually' : undefined,
            originalPriceTitle: isAnnual ? `$${p.price}/month` : undefined,
            annualSavings: isAnnual ? `Save $${annualSavings}/year` : undefined,
            tokensInPackage: p.tokens,
            onClick: () => navigate(`/${ROUTING.SIGNUP}?plan=${encodeURIComponent(p.id)}`),
            onCancelClick: () => {},
            isPopular: p.id === 'growth',
            packageButtonLocale: 'billing.other.getStarted',
          } as PackageConfig;
        }),
      ];
      publicCards.forEach((c, i) => {
        if (!c.isFree) c.bgColor = colors[i % colors.length];
      });
      return publicCards;
    }

    const freePackage = {
      id: 'free',
      isActive: !me?.subscriptions?.[0]?.subscription_id,
      bgColor: blue[50],
      title: 'billing.packageTitle.free',
      subtitle: 'billing.packageText.free',
      packagePriceTitle: 'billing.price.free',
      isFree: true,
      tokensInPackage: BILLING_FREE_TOKENS_AMOUNT,
      onClick: () => {},
      onCancelClick: () => {},
      isExpired: isTrialExpired(me?.subscriptions?.[0]?.created_at as string),
    };

    const packageConfig =
      (packagesData as GetPackagesResponse)?.data?.map(pkg => {
        const price = convertedPrices.find(priceItem => priceItem.id === pkg.default_price);
        const tokensInPackage = Number(pkg.metadata.tokens);

        const isActive =
          ((subscriptionData as GetSubscriptionsResponse)?.plan.id === pkg.default_price && !subscriptionError) ||
          ((subscriptionData as GetSubscriptionsResponse)?.metadata.original_id === pkg.default_price &&
            !subscriptionError);

        if (isActive && price) {
          setActivePrice(+price.unit_amount_decimal);
        }

        // Check if switching from annual to monthly (needs contact support)
        const isFromActiveAnnualToMonthlySubscription =
          period === PERIOD.month && (subscriptionData as GetSubscriptionsResponse)?.plan?.interval === PERIOD.year;

        // Check if downgrading annual subscription
        const isDowngradeAnnualSubscription =
          period === PERIOD.year && !isActive && price && +price.unit_amount_decimal < activePrice;

        // Build price title with period indicator
        const priceTitle = price ? `${price.unit_amount}/month` : '';
        const isAnnual = period === PERIOD.year;
        const priceSubtitle = price && isAnnual ? 'Billed annually' : undefined;
        // Strikethrough + savings for annual: original_unit_amount is preserved
        // (in cents) on convertedPrices so we don't have to round-trip the
        // discount calculation here.
        const originalPriceTitle =
          price && isAnnual && price.original_unit_amount
            ? `${formatCurrency(convertFromCents(price.original_unit_amount))}/month`
            : undefined;
        const annualSavings =
          price && isAnnual && price.original_unit_amount
            ? `Save ${formatCurrency(convertFromCents((price.original_unit_amount - Number(price.unit_amount_decimal)) * 12))}/year`
            : undefined;

        return {
          id: pkg.id,
          isActive,
          bgColor: grey[200],
          title: pkg.name,
          subtitle: pkg.description,
          packagePriceTitle: priceTitle,
          packagePriceSubtitle: priceSubtitle,
          originalPriceTitle,
          annualSavings,
          tokensInPackage,
          onClick: async () => {
            // Public visitor: send them to signup with a hint about which plan
            // they wanted, so we can resume the checkout after registration.
            if (!isLoggedIn) {
              navigate(`/${ROUTING.SIGNUP}?plan=${encodeURIComponent(pkg.id)}`);
              return;
            }
            if (price) {
              if (me?.subscriptions?.[0]?.stripe_customer_id) {
                if (isFromActiveAnnualToMonthlySubscription || isDowngradeAnnualSubscription) {
                  return NiceModal.show(ConfirmModalId, {
                    title: t('billing.pricingModalTitle'),
                    subtitle: t('billing.pricingModalSubtitle', { email: CONTACT_US_EMAIL }),
                    singleButton: true,
                  });
                }
              }
              if (isActive) {
                const result = await NiceModal.show(BillingConfirmModalId, {
                  price: +price.unit_amount_decimal,
                  tokens: tokensInPackage,
                  description: pkg.description
                    .split(';')
                    .map(desc => desc.trim())
                    .filter(desc => desc),
                  period,
                });
                if (result) {
                  await handleTopup(String(price.id));
                  snackbar.enqueueSnackbar(t('dashboard.tokens.topup', { tokens: tokensInPackage }), {
                    variant: SUCCESS,
                  });
                  return;
                }
              }
              handleCheckout(price.id);
            }
          },
          onCancelClick: async (e: MouseEvent) => {
            e.stopPropagation();
            const result = await NiceModal.show(ConfirmModalId, {
              title: 'billing.other.cancelSubscriptionModalText',
            });
            if (result) handleCancelSubscription();
          },
          isLoading: isLoadingMutation,
          isPopular: +pkg.metadata.tokens === BILLING_GROWTH_TOKENS_AMOUNT,
          packageButtonLocale: !isLoggedIn
            ? 'billing.other.getStarted'
            : Number(price?.unit_amount_decimal) > activePrice
              ? 'billing.other.upgradePlan'
              : 'billing.other.changePlan',
          isDisabledPlanSwitch: false, // Allow switching between monthly/annual
        };
      }) || [];

    const hasActivePackage = packageConfig.some(pkg => pkg.isActive);

    const sortedPackages = hasActivePackage
      ? packageConfig.sort((a, b) => a.tokensInPackage - b.tokensInPackage)
      : [freePackage, ...packageConfig].sort((a, b) => a.tokensInPackage - b.tokensInPackage);

    if (me?.subscriptions?.[0]?.stripe_customer_id && !hasActivePackage) {
      sortedPackages.shift();
    }

    if (!me?.subscriptions?.[0]?.subscription_id) {
      setActivePrice(0);
    }

    (sortedPackages as unknown as SortedPackage[]).forEach((pkg, index: number) => {
      if (!pkg.isFree) {
        pkg.bgColor = colors[index % colors.length];
      }
    });

    return sortedPackages.filter(p => p.packagePriceTitle);
  }, [
    me?.subscriptions,
    packagesData,
    convertedPrices,
    subscriptionData,
    subscriptionError,
    isLoadingMutation,
    activePrice,
    period,
    handleCheckout,
    handleTopup,
    snackbar,
    t,
    handleCancelSubscription,
    isLoggedIn,
    navigate,
  ]);

  return {
    config,
    isLoadingQueries,
    packagesData,
    pricesData,
    subscriptionData,
    handleTopup,
    handleCheckout,
    isLoadingMutation,
  };
};
